/**
 * FormD.jsx — Production Levels & Attributed Emissions (Sheet D_Processes)
 *
 * Revision (Jun 2026) — full UI redesign matching Forms A/B/C aesthetic:
 *
 *   1. Slim single-line header — "Production Levels & Attributed Emissions"
 *      (no "Form D", no template name, no banner)
 *   2. AuditSidebar wrapper, same as A/B/C
 *   3. Process tabs at the top (P1 / P2 / P3 ...) — one process visible at a
 *      time. Each tab shows its own % completeness badge.
 *   4. Within each process: 3 collapsible section cards (single scroll,
 *      no nested tabs):
 *        - Card 1: Production Levels & Material Balance (a, b, c, d, e)
 *        - Card 2: Emissions Configuration (g + applicable elements)
 *        - Card 3: Heat, Gases & Electricity (conditional fields)
 *   5. 8-row production levels table → show first 2 rows by default + "show
 *      all 8" expander. Same for 9-row consumed-in-other-processes.
 *   6. Sticky right panel — SEE live values in the soft-card + accent-stripe
 *      style from Form B/C (white-to-blue gradient, vertical accent stripe,
 *      dark navy numbers, SEE total as the focal point).
 *   7. Color palette unified with A/B/C:
 *        - white = user-fill
 *        - lblue (#eff6ff) = auto-filled / auto-calculated
 *        - lgrey (#f1f5f9) = disabled (applicable element FALSE)
 *   8. Applicable elements: TRUE/FALSE dropdowns → modern toggle switches.
 *   9. Sector-aware Indirect Emissions toggle:
 *        - Cement / Fertilisers → forced ON + locked (Annex IV requires it)
 *        - Iron & steel / Aluminium / Hydrogen → default OFF, user-toggleable
 *  10. Aluminium reminder near "directly attributable emissions".
 *  11. Control balance row (e) restyled — soft green when balanced, soft red
 *      when off. Same logic, new palette.
 *  12. NEXT button between processes; Save & Continue on final process.
 *
 * Revision (Jul 2026) — Production Details (b) corrections per senior:
 *   B1. (ii) Share of total under (a) — now AUTO-CALCULATED (read-only).
 *       Formula: (amountsProducedForMarket / productionLevelsTotal) * 100
 *   B2. Info banner when share < 100 → prompt user to complete (c) & (d).
 *   B3. (iii) Total production only for the market? — AUTO-DERIVED (Yes/No,
 *       read-only) from whether share == 100%.
 *
 * Behaviour preserved EXACTLY:
 *   - scaffoldProcess / getRoutesForProcess / getIncludedGoods / getPrecursors
 *   - All engine calls: D.totalProductionVolume, D.calculateSEE
 *   - Firestore onSnapshot merge logic
 *   - update() path-splitting handler
 *   - calculateFormDProgress + per-process required fields
 *   - saveToFirestore payload
 *   - Navigation routes /form-c/ and /form-e/
 **/
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, CircularProgress, Tooltip } from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { firestore as db } from "./firebase.config";
import { doc, setDoc, onSnapshot } from "firebase/firestore";
import { useFormContext } from "../context/FormContext";
import { D } from "../utils/CbamFormulas";
import { UNIT_GROUPS, UNITS } from "../utils/Cbamunits";
import AuditSidebar from "../components/AuditSidebar";
import useAuditProgress from "../hooks/useAuditProgress";
import {
  calculateRequiredProgressForRows,
  FORM_D_REQUIRED_FIELDS,
} from "../utils/CbamProgress";

// ─── Sector-aware logic (verified against CBAM Annex IV / Annex II) ───────────
const sectorIndirectRule = (sector) => {
  if (sector === "Cement" || sector === "Fertilisers") {
    return { force: true, locked: true, hint: "Required for this sector (Annex IV)" };
  }
  if (sector === "Iron and steel" || sector === "Aluminium" || sector === "Hydrogen") {
    return { force: false, locked: false, hint: "Annex II — optional reporting" };
  }
  return { force: false, locked: false, hint: "" };
};
const isAluminiumSector = (sector) => sector === "Aluminium";

// ─── CBAM goods routes (unchanged) ────────────────────────────────────────────
const CBAM_GOODS = [
  { name: "Cement", routes: ["All production routes"] },
  { name: "Cement clinker", routes: ["All production routes"] },
  { name: "Calcined clays", routes: ["All production routes"] },
  { name: "Aluminous cement", routes: ["All production routes"] },
  { name: "Iron or steel products", routes: ["All production routes"] },
  { name: "Crude steel", routes: ["Basic oxygen steelmaking", "Electric arc furnace", "Other production routes", "Unknown production routes"] },
  { name: "Direct reduced iron", routes: ["All production routes"] },
  { name: "Pig iron", routes: ["Blast furnace route", "Smelting reduction", "Other production routes", "Unknown production routes"] },
  { name: "Alloys (FeMn, FeCr, FeNi)", routes: ["All production routes"] },
  { name: "Sintered Ore", routes: ["All production routes"] },
  { name: "Hydrogen", routes: ["Steam reforming and partial oxidation", "Electrolysis of water", "Chlor-Alkali electrolysis and production of chlorates", "Other production routes", "Unknown production routes"] },
  { name: "Ammonia", routes: ["Haber-Bosch process with steam reforming of natural gas or biogas", "Haber-Bosch process with gasification of coal or other fuels", "Other production routes", "Unknown production routes"] },
  { name: "Nitric acid", routes: ["All production routes"] },
  { name: "Urea", routes: ["All production routes"] },
  { name: "Mixed fertilisers", routes: ["All production routes"] },
  { name: "Aluminium products", routes: ["All production routes"] },
  { name: "Unwrought aluminium", routes: ["Primary (electrolytic) smelting", "Secondary melting (recycling)", "Other production routes", "Unknown production routes"] },
  { name: "Electricity (export to EU)", routes: ["All production routes"] },
];
const ROUTES_BY_CATEGORY = Object.fromEntries(CBAM_GOODS.map((g) => [g.name, g.routes]));

// ─── Scaffold helpers (unchanged) ─────────────────────────────────────────────
const getRoutesForProcess = (formAProcess, aggregatedGoods) => {
  const cat = formAProcess?.aggregatedGoodsCategory;
  if (!cat) return [];
  const fromFormA = (aggregatedGoods || [])
    .filter((g) => g.category === cat)
    .flatMap((g) => [g.primaryRoute, ...(g.routes || []), g.route1, g.route2, g.route3, g.route4, g.route5, g.route6])
    .filter((r) => r && r.trim());
  return fromFormA.length > 0 ? [...new Set(fromFormA)] : ROUTES_BY_CATEGORY[cat] || [];
};

const getIncludedGoods = (p) =>
  ["inc1", "inc2", "inc3", "inc4", "inc5", "inc6"].map((k) => p?.[k]).filter(Boolean);

const getPrecursors = (processId, purchasedPrecursors) =>
  (purchasedPrecursors || []).filter((pp) => pp.linkedProcessId === processId);

const scaffoldProcess = (formAProcess, aggregatedGoods, purchasedPrecursors) => {
  const routes = getRoutesForProcess(formAProcess, aggregatedGoods);
  return {
    processId: formAProcess.id,
    title: formAProcess.name || "",
    aggregatedGoodsCategory: formAProcess.aggregatedGoodsCategory || "",
    availableRoutes: routes,
    includedGoods: getIncludedGoods(formAProcess),
    linkedPrecursors: getPrecursors(formAProcess.id, purchasedPrecursors),
    productionLevels: Array.from({ length: 8 }).map((_, i) => ({
      totalProductionLevel: routes[i] || "",
      productionRoute: routes[i] || "",
      unit: "",
      amounts: "",
    })),
    productionLevelsTotal: "",
    productionDetails: {
      producedForMarket: "",
      amountsProducedForMarket: "",
      shareOfTotalProducedForMarket: "",
      totalProductionOnlyForMarket: "",
    },
    consumedOtherProcesses: Array.from({ length: 9 }).map(() => ({ processId: "", unit: "", amounts: "" })),
    consumedNonCbam: { unit: "", amounts: "" },
    control: "",
    applicableElements: {
      measurableHeat: "FALSE",
      wasteGases: "FALSE",
      indirectEmissions: "FALSE",
    },
    directlyAttributableEmissions: "",
    measurableHeat: { amountImported: "", amountExported: "", emissionFactorImported: "", emissionFactorExported: "" },
    wasteGases:    { amountImported: "", amountExported: "", emissionFactorImported: "", emissionFactorExported: "" },
    electricityConsumption: { consumption: "", emissionFactor: "", source: "" },
    electricityExported:    { amountsExported: "", emissionFactor: "" },
    see_direct: 0, see_indirect: 0, see_total: 0,
    dirEm_net: 0, indir_em: 0,
  };
};

// ─── Styles ───────────────────────────────────────────────────────────────────
const STYLES = `
  .fd { font-family: 'IBM Plex Sans', sans-serif; font-size: 13px; color: #161616; }
  .fd-header-slim {
    background: #fff;
    border-bottom: 2px solid #2563eb;
    padding: 14px clamp(20px, 3vw, 32px);
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .fd-header-slim .title {
    font-size: 17px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.2px;
  }
  .fd-body {
    width: 100%;
    max-width: 1600px;
    margin: 0 auto;
    padding: 14px clamp(20px, 3vw, 32px) 20px;
  }
  .fd-sector-bar {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 11px;
    color: #475569;
    margin-bottom: 10px;
  }
  .fd-sector-chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: #eff6ff;
    color: #1e40af;
    border: 1px solid #bfdbfe;
    padding: 2px 8px;
    border-radius: 4px;
    font-weight: 600;
    font-size: 11px;
  }
  .fd-sector-note { font-style: italic; color: #64748b; }
  .fd-tabs {
    display: flex;
    gap: 4px;
    border-bottom: 2px solid #2563eb;
    margin-bottom: 14px;
    overflow-x: auto;
  }
  .fd-tab {
    display: inline-flex;
    align-items: center;
    gap: 9px;
    padding: 9px 16px;
    font-size: 13px;
    color: #64748b;
    cursor: pointer;
    border: 1px solid transparent;
    border-bottom: none;
    background: #fff;
    border-top-left-radius: 6px;
    border-top-right-radius: 6px;
    white-space: nowrap;
    user-select: none;
    transition: background 0.15s, color 0.15s;
  }
  .fd-tab:hover:not(.active) { background: #f1f5f9; color: #1e293b; }
  .fd-tab.active {
    color: #0f172a;
    font-weight: 700;
    border-color: #cbd5e1;
    background: #fff;
  }
  .fd-tab-num {
    font-family: 'IBM Plex Mono', monospace;
    font-size: 11px;
    font-weight: 700;
    background: #e2e8f0;
    color: #475569;
    padding: 2px 7px;
    border-radius: 4px;
    letter-spacing: 0.5px;
  }
  .fd-tab.active .fd-tab-num { background: #2563eb; color: #fff; }
  .fd-tab-title { max-width: 180px; overflow: hidden; text-overflow: ellipsis; }
  .fd-tab-badge {
    padding: 2px 8px;
    border-radius: 99px;
    font-size: 11px;
    font-weight: 700;
    background: #E0E0E0;
    color: #555;
  }
  .fd-tab-badge.done  { background: #DCFCE7; color: #15803D; }
  .fd-tab-badge.warn  { background: #FEF3C7; color: #D97706; }
  .fd-tab-badge.empty { background: #FEE2E2; color: #DC2626; }
  .fd-grid {
    display: grid;
    grid-template-columns: 1fr 320px;
    gap: 16px;
  }
  @media (max-width: 1100px) {
    .fd-grid { grid-template-columns: 1fr; }
  }
  .fd-content { min-width: 0; }
  .fd-sticky-col { position: relative; }
  .fd-sticky {
    position: sticky;
    top: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .fd-process-meta {
    background: #fff;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    padding: 10px 14px;
    margin-bottom: 12px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px 16px;
  }
  .fd-meta-item .lbl {
    font-size: 9px;
    color: #64748b;
    text-transform: uppercase;
    font-weight: 700;
    letter-spacing: 0.5px;
    margin-bottom: 2px;
  }
  .fd-meta-item .val {
    font-size: 13px;
    color: #1e293b;
    font-weight: 600;
  }
  .fd-meta-routes {
    grid-column: 1 / -1;
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    align-items: center;
    font-size: 10px;
    color: #64748b;
  }
  .fd-route-chip {
    background: #eff6ff;
    color: #1e40af;
    border: 1px solid #bfdbfe;
    padding: 1px 7px;
    border-radius: 3px;
    font-size: 10px;
    font-weight: 500;
  }
  .fd-subtabs {
    display: flex;
    gap: 4px;
    border-bottom: 1px solid #cbd5e1;
    margin-bottom: 12px;
  }
  .fd-subtab {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 7px 14px;
    font-size: 12px;
    color: #64748b;
    cursor: pointer;
    border: 1px solid transparent;
    border-bottom: none;
    background: #fff;
    border-top-left-radius: 5px;
    border-top-right-radius: 5px;
    white-space: nowrap;
    user-select: none;
    transition: background 0.15s, color 0.15s;
  }
  .fd-subtab:hover:not(.active) { background: #f8fafc; color: #1e293b; }
  .fd-subtab.active {
    color: #0f172a;
    font-weight: 700;
    border-color: #cbd5e1;
    border-bottom: 1px solid #fff;
    background: #fff;
    margin-bottom: -1px;
  }
  .fd-subtab-num {
    font-family: 'IBM Plex Mono', monospace;
    font-size: 10px;
    font-weight: 700;
    background: #e2e8f0;
    color: #475569;
    padding: 1px 6px;
    border-radius: 3px;
    letter-spacing: 0.4px;
  }
  .fd-subtab.active .fd-subtab-num { background: #2563eb; color: #fff; }
  .fd-subtab-meta {
    font-size: 10px;
    color: #94a3b8;
    font-weight: 500;
  }
  .fd-subtab-panel {
    background: #fff;
    border: 1px solid #cbd5e1;
    border-top: none;
    border-radius: 0 0 6px 6px;
    padding: 14px;
    margin-bottom: 12px;
    box-shadow: 0 1px 2px rgba(0,0,0,0.03);
  }
  .fd-element-block {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 5px;
    padding: 10px 12px;
    margin-bottom: 10px;
  }
  .fd-element-block:last-child { margin-bottom: 0; }
  .fd-element-fields {
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px dashed #cbd5e1;
  }
  .fd-sub-label {
    font-size: 11px;
    font-weight: 700;
    color: #1e293b;
    margin: 10px 0 6px;
    padding-bottom: 3px;
    border-bottom: 2px solid #2563eb;
    display: inline-block;
  }
  .fd-sub-label:first-child { margin-top: 0; }
  .fd-rows {
    display: grid;
    gap: 4px;
    margin-bottom: 6px;
  }
  .fd-row-head, .fd-row {
    display: grid;
    gap: 6px;
    align-items: center;
  }
  .fd-row-head {
    font-size: 9px;
    font-weight: 700;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    padding: 0 4px;
  }
  .fd-row { padding: 2px 0; }
  .fd-rownum {
    text-align: center;
    color: #94a3b8;
    font-size: 10px;
    font-family: 'IBM Plex Mono', monospace;
  }
  .fd-input, .fd-select {
    border: 1px solid #cbd5e1;
    background: #fff;
    padding: 6px 8px;
    border-radius: 4px;
    font-family: 'IBM Plex Sans', sans-serif;
    font-size: 13px;
    color: #161616;
    outline: none;
    width: 100%;
    box-sizing: border-box;
    transition: border-color 0.15s, background 0.15s;
  }
  .fd-input:focus, .fd-select:focus { border-color: #2563eb; }
  .fd-input.lblue, .fd-select.lblue {
    background: #eff6ff;
    border-color: #bfdbfe;
  }
  .fd-input.lblue:read-only { cursor: default; }
  .fd-input.lgrey, .fd-select.lgrey {
    background: #f1f5f9;
    border-color: #e2e8f0;
    color: #94a3b8;
    cursor: not-allowed;
  }
  .fd-expander {
    background: transparent;
    border: 1px dashed #cbd5e1;
    color: #64748b;
    padding: 6px 12px;
    font-size: 11px;
    font-weight: 600;
    border-radius: 4px;
    cursor: pointer;
    width: 100%;
    text-align: center;
    transition: background 0.15s, color 0.15s;
  }
  .fd-expander:hover {
    background: #eff6ff;
    border-color: #93c5fd;
    color: #2563eb;
  }
  .fd-field {
    display: grid;
    grid-template-columns: 1fr 100px 1fr;
    gap: 8px;
    align-items: center;
    padding: 4px 0;
  }
  .fd-field-label {
    font-size: 12px;
    color: #475569;
    font-weight: 500;
  }
  .fd-field-unit {
    font-size: 10px;
    color: #64748b;
    text-align: center;
    background: #f1f5f9;
    padding: 4px 6px;
    border-radius: 3px;
  }
  .fd-control {
    border: 1px solid;
    border-radius: 5px;
    padding: 10px 14px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 8px;
  }
  .fd-control.ok  {
    background: #f0fdf4;
    border-color: #86efac;
    color: #15803d;
  }
  .fd-control.bad {
    background: #fef2f2;
    border-color: #fca5a5;
    color: #b91c1c;
  }
  .fd-control .label {
    font-size: 11px;
    font-weight: 600;
  }
  .fd-control .formula {
    font-size: 10px;
    color: #64748b;
    margin-top: 2px;
  }
  .fd-control .val {
    font-family: 'IBM Plex Mono', monospace;
    font-weight: 700;
    font-size: 15px;
  }
  .fd-toggle-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 10px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 5px;
    margin-bottom: 6px;
  }
  .fd-toggle-row .label {
    font-size: 12px;
    font-weight: 600;
    color: #1e293b;
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .fd-toggle-row .sublabel {
    font-size: 10px;
    color: #64748b;
    font-style: italic;
    margin-top: 2px;
  }
  .fd-toggle {
    position: relative;
    width: 38px;
    height: 22px;
    background: #cbd5e1;
    border-radius: 11px;
    cursor: pointer;
    transition: background 0.18s;
    border: none;
    flex-shrink: 0;
  }
  .fd-toggle.on { background: #2563eb; }
  .fd-toggle.locked { cursor: not-allowed; opacity: 0.85; }
  .fd-toggle::before {
    content: "";
    position: absolute;
    top: 2px; left: 2px;
    width: 18px; height: 18px;
    background: #fff;
    border-radius: 50%;
    transition: transform 0.18s;
    box-shadow: 0 1px 3px rgba(0,0,0,0.2);
  }
  .fd-toggle.on::before { transform: translateX(16px); }
  .fd-alu-hint {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 10px;
    color: #1e40af;
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    padding: 5px 9px;
    border-radius: 4px;
    margin-bottom: 6px;
  }
  /*  Share warning banner — shown when share < 100 */
  .fd-share-warn {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 9px 12px;
    background: #FFF4E0;
    border: 1px solid #F5D08A;
    border-left: 3px solid #F59E0B;
    border-radius: 4px;
    margin: 8px 0 10px;
    font-size: 11.5px;
    color: #7C4A03;
    line-height: 1.5;
  }
  .fd-share-warn strong { color: #713F00; }
  .fd-see-card {
    position: relative;
    background: linear-gradient(180deg, #f8fbff 0%, #eff6ff 100%);
    border: 1px solid #dbeafe;
    border-radius: 6px;
    padding: 14px 14px 14px 20px;
    overflow: hidden;
  }
  .fd-see-card::before {
    content: "";
    position: absolute;
    left: 0; top: 0; bottom: 0;
    width: 4px;
    background: linear-gradient(180deg, #1e40af 0%, #2563eb 50%, #06b6d4 100%);
  }
  .fd-see-card-title {
    font-size: 9px;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    font-weight: 700;
    margin-bottom: 10px;
  }
  .fd-see-item {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    padding: 5px 0;
    border-bottom: 1px solid rgba(203,213,225,0.5);
  }
  .fd-see-item:last-of-type { border-bottom: none; }
  .fd-see-item .k {
    font-size: 10px;
    color: #475569;
    font-weight: 600;
  }
  .fd-see-item .v {
    font-family: 'IBM Plex Mono', monospace;
    font-weight: 700;
    font-size: 13px;
    color: #1e293b;
  }
  .fd-see-item .v.zero { opacity: 0.4; }
  .fd-see-item .unit {
    font-size: 9px;
    color: #94a3b8;
    margin-left: 4px;
  }
  .fd-see-grand {
    border-top: 1px solid #cbd5e1;
    margin-top: 10px;
    padding-top: 10px;
  }
  .fd-see-grand .k {
    font-size: 10px;
    color: #475569;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 4px;
  }
  .fd-see-grand .v {
    font-family: 'IBM Plex Mono', monospace;
    font-weight: 700;
    font-size: 22px;
    color: #1e40af;
    letter-spacing: -0.4px;
  }
  .fd-see-grand .v.zero { color: #94a3b8; }
  .fd-see-grand .unit { font-size: 10px; color: #64748b; margin-left: 4px; }
  .fd-footer-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 16px;
    padding-top: 12px;
    border-top: 1px solid #e2e8f0;
  }
  .fd-btn-prev {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 9px 18px;
    background: #fff; color: #475569;
    border: 1px solid #cbd5e1;
    font-family: inherit; font-size: 13px; font-weight: 600;
    cursor: pointer; border-radius: 4px;
  }
  .fd-btn-prev:hover:not(:disabled) { background: #f1f5f9; }
  .fd-btn-prev:disabled { opacity: 0.4; cursor: not-allowed; }
  .fd-btn-save {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 10px 24px;
    background: #2563eb; color: #fff;
    border: none;
    font-family: inherit; font-size: 14px; font-weight: 700;
    cursor: pointer; border-radius: 4px;
    transition: background 0.15s;
  }
  .fd-btn-save:hover { background: #1d4ed8; }
  .fd-btn-save:disabled { background: #cbd5e1; cursor: not-allowed; }
  .fd-toast {
    position: fixed; top: 88px; right: 20px; z-index: 1400;
    background: #22c55e; color: #fff;
    padding: 12px 20px; font-size: 13px; font-weight: 600;
    border-radius: 6px;
    box-shadow: 0 4px 16px rgba(0,0,0,0.15);
  }
  .fd-toast.error { background: #dc2626; }
  .fd-empty {
    display: flex; flex-direction: column; align-items: center;
    gap: 12px; padding: 80px 20px; text-align: center;
  }
  .fd-empty-title { font-size: 17px; color: #475569; font-weight: 600; }
  .fd-empty-sub   { font-size: 13px; color: #64748b; }
`;

// ─── Small helpers ────────────────────────────────────────────────────────────
const fmt = (v) => {
  if (v === "" || v === null || v === undefined) return "—";
  const n = Number(v);
  if (!isFinite(n)) return "—";
  return n.toLocaleString("en-US", { maximumFractionDigits: 4 });
};

//  Compute share % from amountsProducedForMarket and total production.
//  Returns a number (0-100+) or null if not computable.
const computeShare = (amountsProducedForMarket, productionLevelsTotal) => {
  const amt = Number(amountsProducedForMarket);
  const tot = Number(productionLevelsTotal);
  if (!isFinite(amt) || !isFinite(tot) || tot <= 0) return null;
  return (amt / tot) * 100;
};

//  Format share for display, 2 dp.
const fmtShare = (share) => {
  if (share === null) return "";
  return share.toLocaleString("en-US", { maximumFractionDigits: 2 });
};
//  True if any row in (c) Consumed-in-other-processes, or (d) Consumed-for-
//  non-CBAM-goods, has a non-empty, non-zero amount entered.
const hasConsumptionEntries = (proc) => {
  const cHasEntry = (proc.consumedOtherProcesses || []).some((r) => {
    const n = Number(r.amounts);
    return r.amounts !== "" && r.amounts != null && isFinite(n) && n > 0;
  });
  const dAmt = Number(proc.consumedNonCbam?.amounts);
  const dHasEntry =
    proc.consumedNonCbam?.amounts !== "" &&
    proc.consumedNonCbam?.amounts != null &&
    isFinite(dAmt) && dAmt > 0;
  return cHasEntry || dHasEntry;
};

// ─── Component ════════════════════════════════════════════════════════════════
const FormD = () => {
  const navigate = useNavigate();
  const { auditId } = useParams();
  const { formsData } = useAuditProgress(auditId);
  const { allFormData, updateForm } = useFormContext();

  const formAProcesses = allFormData?.formA?.relevantProcesses || [];
  const installSector  = allFormData?.formA?.sector || "";
  const indirectRule   = sectorIndirectRule(installSector);
  const isAluminium    = isAluminiumSector(installSector);

  const [processes, setProcesses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving,  setIsSaving]  = useState(false);
  const [activeProcessIdx, setActiveProcessIdx] = useState(0);
  const [toast, setToast] = useState(null);

  const [subTab, setSubTab] = useState({});
  const getSubTab = (pi) => subTab[pi] || "balance";
  const setSubTabFor = (pi, t) => setSubTab(p => ({ ...p, [pi]: t }));

  const [expanded, setExpanded] = useState({});

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2500);
  };

  const getRequiredFieldsForProcess = (proc) => {
    const fields = [...FORM_D_REQUIRED_FIELDS.alwaysRequired];
    if (proc?.applicableElements?.measurableHeat === "TRUE") fields.push(...FORM_D_REQUIRED_FIELDS.measurableHeat);
    if (proc?.applicableElements?.wasteGases === "TRUE") fields.push(...FORM_D_REQUIRED_FIELDS.wasteGases);
    if (proc?.applicableElements?.indirectEmissions === "TRUE") fields.push(...FORM_D_REQUIRED_FIELDS.indirectEmissions);
    return fields;
  };

  const getProcessProgress = (proc) =>
    calculateRequiredProgressForRows([proc], getRequiredFieldsForProcess(proc));

  useEffect(() => {
    if (!auditId) return;
    const ref = doc(db, "auditReports", auditId, "form_d", "main");
    const unsub = onSnapshot(ref, (snap) => {
      const stored = snap.exists() ? snap.data() : {};
      const currentProcesses = allFormData?.formA?.relevantProcesses || [];
      const currentAggGoods = allFormData?.formA?.aggregatedGoods || [];
      const currentPrecursors = allFormData?.formA?.purchasedPrecursors || [];
      const merged = currentProcesses.map((p) => {
        const scaffold = scaffoldProcess(p, currentAggGoods, currentPrecursors);
        const existing = (stored?.processes || []).find((pr) => pr.processId === p.id);
        const mergedProc = existing
          ? {
              ...scaffold,
              ...existing,
              processId: p.id,
              title: p.name || existing.title || "",
              aggregatedGoodsCategory: p.aggregatedGoodsCategory || existing.aggregatedGoodsCategory || "",
              availableRoutes: scaffold.availableRoutes,
              includedGoods: scaffold.includedGoods,
              linkedPrecursors: scaffold.linkedPrecursors,
              productionLevels: scaffold.productionLevels.map((row, i) => ({
                ...row,
                unit: existing.productionLevels?.[i]?.unit || "",
                amounts: existing.productionLevels?.[i]?.amounts || "",
              })),
            }
          : scaffold;
        if (indirectRule.force) {
          mergedProc.applicableElements = {
            ...mergedProc.applicableElements,
            indirectEmissions: "TRUE",
          };
        }
        const autoTotal = D.totalProductionVolume(mergedProc.productionLevels);
        return {
          ...mergedProc,
          productionLevelsTotal: autoTotal,
          ...D.calculateSEE({ ...mergedProc, productionLevelsTotal: autoTotal }),
        };
      });
      setProcesses(merged);
      setIsLoading(false);
    }, (err) => { console.error(err); setIsLoading(false); });
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auditId, indirectRule.force]);

  //  Keep the persisted (b)(ii) share and (b)(iii) auto-flag fields in sync
  //  with their derived values, so Firestore / exports read the same value
  //  the UI shows. Guarded by a value-diff check to avoid an infinite loop.
  useEffect(() => {
    setProcesses((prev) => {
      let changed = false;
      const next = prev.map((proc) => {
        const shareNum = computeShare(
          proc.productionDetails?.amountsProducedForMarket,
          proc.productionLevelsTotal
        );
        const shareStr = fmtShare(shareNum);
        const shareIsHundred = shareNum !== null && Math.abs(shareNum - 100) < 0.001;
        const onlyMarket = hasConsumptionEntries(proc)
          ? "No"
          : (shareNum === null ? "" : (shareIsHundred ? "Yes" : "No"));

        if (
          proc.productionDetails?.shareOfTotalProducedForMarket === shareStr &&
          proc.productionDetails?.totalProductionOnlyForMarket === onlyMarket
        ) {
          return proc;
        }
        changed = true;
        return {
          ...proc,
          productionDetails: {
            ...proc.productionDetails,
            shareOfTotalProducedForMarket: shareStr,
            totalProductionOnlyForMarket: onlyMarket,
          },
        };
      });
      return changed ? next : prev;
    });
  }, [processes]);

  const update = (pi, path, value) => {
    setProcesses((prev) => {
      const copy = structuredClone(prev);
      const parts = path.split(".");
      let cur = copy[pi];
      for (let i = 0; i < parts.length - 1; i++) {
        if (!(parts[i] in cur)) cur[parts[i]] = {};
        cur = cur[parts[i]];
      }
      cur[parts[parts.length - 1]] = value;
      const calc = D.calculateSEE(copy[pi]);
      copy[pi] = {
        ...copy[pi],
        productionLevelsTotal: calc.productionLevelsTotalAuto ?? D.totalProductionVolume(copy[pi].productionLevels),
        marketShareFraction: calc.marketShareFraction,
        see_direct: calc.see_direct,
        see_indirect: calc.see_indirect,
        see_total: calc.see_total,
        dirEm_net: calc.dirEm_net,
        indir_em: calc.indir_em,
      };
      return copy;
    });
  };

  const calculateFormDProgress = () => {
    const processSections = processes.map((proc, index) => ({
      key: `process_${index + 1}`,
      title: `Production process ${index + 1}`,
      ...getProcessProgress(proc),
    }));
    const editableTotal  = processSections.reduce((s, x) => s + x.total, 0);
    const editableFilled = processSections.reduce((s, x) => s + x.filled, 0);
    const progress = editableTotal === 0 ? 0 : Math.round((editableFilled / editableTotal) * 100);
    const sections = processSections.reduce((acc, section) => {
      acc[section.key] = { title: section.title, filled: section.filled, total: section.total, progress: section.progress };
      return acc;
    }, {});
    return {
      editableTotal, editableFilled, progress,
      status: progress >= 100 ? "Completed" : progress > 0 ? "In Progress" : "Not Started",
      sections,
    };
  };

  const saveToFirestore = async () => {
    const progressMeta = calculateFormDProgress();
    await setDoc(doc(db, "auditReports", auditId, "form_d", "main"), {
      processes, progressMeta, updatedAt: new Date(),
    });
    updateForm("formD", { processes, progressMeta });
  };

  const handleSaveAndNext = async () => {
    setIsSaving(true);
    try {
      await saveToFirestore();
      showToast("Form D saved");
      setTimeout(() => navigate(`/form-e/${auditId}`), 500);
    } catch (e) {
      console.error(e);
      showToast("Save failed", "error");
    } finally { setIsSaving(false); }
  };

  const handleBack = async () => {
    setIsSaving(true);
    try { await saveToFirestore(); navigate(`/form-c/${auditId}`); }
    catch (e) { console.error(e); navigate(`/form-c/${auditId}`); }
    finally { setIsSaving(false); }
  };

  const handleNextProcess = async () => {
    setIsSaving(true);
    try {
      await saveToFirestore();
      if (activeProcessIdx < processes.length - 1) {
        setActiveProcessIdx(activeProcessIdx + 1);
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
      showToast("Saved");
    } catch (e) { console.error(e); showToast("Save failed", "error"); }
    finally { setIsSaving(false); }
  };

  const isExpanded = (pi, table) => !!expanded[`${pi}.${table}`];
  const toggleExpanded = (pi, table) =>
    setExpanded(p => ({ ...p, [`${pi}.${table}`]: !p[`${pi}.${table}`] }));

  const tabBadgeClass = (pct) =>
    pct === 100 ? "done" : pct > 0 ? "warn" : "empty";

  if (isLoading) return (
    <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
      <CircularProgress />
    </Box>
  );

  if (formAProcesses.length === 0) {
    return (
      <>
        <style>{STYLES}</style>
        <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#f8fafc" }}>
          <AuditSidebar displayAuditId={auditId} formsData={formsData} />
          <Box sx={{ flex: 1 }}>
            <div className="fd-header-slim">
              <div className="title">Production Levels &amp; Attributed Emissions</div>
            </div>
            <div className="fd-empty">
              <div className="fd-empty-title">No production processes found in Form A.</div>
              <div className="fd-empty-sub">Complete Form A → Section 4(b) first, then return here.</div>
              <button className="fd-btn-prev" onClick={() => navigate(`/form-a/${auditId}`)}>
                ← Go to Form A
              </button>
            </div>
          </Box>
        </Box>
      </>
    );
  }

  // ─── Render a single process ───────────────────────────────────────────────
  const renderProcess = (proc, pi) => {
    const otherProcs = processes.filter((_, i) => i !== pi);
    const heatOn  = proc.applicableElements?.measurableHeat === "TRUE";
    const wasteOn = proc.applicableElements?.wasteGases === "TRUE";
    const indirOn = proc.applicableElements?.indirectEmissions === "TRUE";
    const seeHasValue = proc.see_total !== 0 && proc.see_total != null;

    const total = Number(proc.productionLevelsTotal) || 0;
    const market = Number(proc.productionDetails?.amountsProducedForMarket) || 0;
    const otherProcsSum = (proc.consumedOtherProcesses || []).reduce((s, r) => s + (Number(r.amounts) || 0), 0);
    const nonCbam = Number(proc.consumedNonCbam?.amounts) || 0;
    const balance = +(total - market - otherProcsSum - nonCbam).toFixed(4);
    const balanceOk = Math.abs(balance) < 0.001;

    //  Share of total — AUTO from (i) amount / total * 100.
    //  Read-only display; original stored field kept in sync for compatibility.
    const shareNum = computeShare(proc.productionDetails?.amountsProducedForMarket, proc.productionLevelsTotal);
    const shareDisplay = fmtShare(shareNum);
    const shareIsHundred = shareNum !== null && Math.abs(shareNum - 100) < 0.001;
    const shareBelowHundred = shareNum !== null && shareNum < 100 - 0.001;

    //  Total production only for market — AUTO-FLAGGED to "No" the moment
    //  (c) or (d) has any entry (per client spec), regardless of whether
    //  share is computable yet. Falls back to share-based derivation
    //  ("Yes" only when share === 100%) when (c)/(d) are empty.
    const forcedFalseByConsumption = hasConsumptionEntries(proc);
    const onlyMarketDisplay = forcedFalseByConsumption
      ? "No"
      : (shareNum === null ? "" : (shareIsHundred ? "Yes" : "No"));

    const prodLevelsToShow = isExpanded(pi, "prod") ? 8 : 2;
    const consumedToShow   = isExpanded(pi, "cons") ? 9 : 2;

    return (
      <div className="fd-grid" key={proc.processId}>
        {/* ─── LEFT: process content ─── */}
        <div className="fd-content">
          <div className="fd-process-meta">
            <div className="fd-meta-item">
              <div className="lbl">Process</div>
              <div className="val">{proc.processId} · {proc.title || "(unnamed)"}</div>
            </div>
            <div className="fd-meta-item">
              <div className="lbl">Aggregated goods</div>
              <div className="val">{proc.aggregatedGoodsCategory || "—"}</div>
            </div>
            {proc.availableRoutes?.length > 0 && (
              <div className="fd-meta-routes">
                <span>Routes from Form A:</span>
                {proc.availableRoutes.map((r) => (
                  <span key={r} className="fd-route-chip">{r}</span>
                ))}
              </div>
            )}
          </div>

          <div className="fd-subtabs">
            <div
              className={`fd-subtab ${getSubTab(pi) === "balance" ? "active" : ""}`}
              onClick={() => setSubTabFor(pi, "balance")}
            >
              <span className="fd-subtab-num">01</span>
              <span>Production &amp; Material Balance</span>
              <span className="fd-subtab-meta">
                {fmt(proc.productionLevelsTotal)} t · {balance} {balanceOk ? "✓" : "✗"}
              </span>
            </div>
            <div
              className={`fd-subtab ${getSubTab(pi) === "emissions" ? "active" : ""}`}
              onClick={() => setSubTabFor(pi, "emissions")}
            >
              <span className="fd-subtab-num">02</span>
              <span>Emissions</span>
              <span className="fd-subtab-meta">
                {[heatOn && "Heat", wasteOn && "Gas", indirOn && "Indirect"]
                  .filter(Boolean).join(" · ") || "no elements"}
              </span>
            </div>
          </div>

          {getSubTab(pi) === "balance" && (
            <div className="fd-subtab-panel">
              {/* (a) Production levels */}
              <div className="fd-sub-label">(a) Production levels</div>
              <div className="fd-rows">
                <div className="fd-row-head" style={{ gridTemplateColumns: "28px 1fr 110px 1fr" }}>
                  <div className="fd-rownum">#</div>
                  <div>Production route</div>
                  <div>Unit</div>
                  <div>Amounts</div>
                </div>
                {proc.productionLevels.slice(0, prodLevelsToShow).map((row, i) => (
                  <div key={i} className="fd-row" style={{ gridTemplateColumns: "28px 1fr 110px 1fr" }}>
                    <div className="fd-rownum">{String(i+1).padStart(2,"0")}</div>
                    <input className="fd-input lblue" readOnly value={row.totalProductionLevel || ""} />
                    <select className="fd-select" value={row.unit || ""}
                      onChange={(e) => update(pi, `productionLevels.${i}.unit`, e.target.value)}>
                      <option value="">—</option>
                      {UNIT_GROUPS.MASS_OR_VOLUME.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                    <input className="fd-input" type="number" value={row.amounts || ""}
                      onChange={(e) => update(pi, `productionLevels.${i}.amounts`, e.target.value)}
                      placeholder="0" />
                  </div>
                ))}
              </div>
              {!isExpanded(pi, "prod") && (
                <button className="fd-expander" onClick={() => toggleExpanded(pi, "prod")}>
                  + Show all 8 rows (6 more)
                </button>
              )}
              {isExpanded(pi, "prod") && (
                <button className="fd-expander" onClick={() => toggleExpanded(pi, "prod")}>
                  − Collapse to first 2 rows
                </button>
              )}

              <div className="fd-field" style={{ gridTemplateColumns: "1fr 100px 1fr", marginTop: 8 }}>
                <div className="fd-field-label" style={{ fontWeight: 700 }}>Total production within installation</div>
                <div className="fd-field-unit">t</div>
                <input className="fd-input lblue" readOnly value={proc.productionLevelsTotal || ""} />
              </div>

              {/* (b) Production details — (ii) and (iii) now auto */}
              <div className="fd-sub-label">(b) Production details</div>
              <div className="fd-field">
                <div className="fd-field-label">i · Produced for the market</div>
                <input className="fd-input" value={proc.productionDetails?.producedForMarket || ""}
                  onChange={(e) => update(pi, "productionDetails.producedForMarket", e.target.value)}
                  placeholder="Unit" />
                <input className="fd-input" type="number"
                  value={proc.productionDetails?.amountsProducedForMarket || ""}
                  onChange={(e) => update(pi, "productionDetails.amountsProducedForMarket", e.target.value)}
                  placeholder="Amount" />
              </div>

              {/*  ii — auto-calculated share, read-only */}
              <div className="fd-field">
                <div className="fd-field-label">ii · Share of total under (a) produced for the market</div>
                <div className="fd-field-unit">%</div>
                <input className="fd-input lblue" readOnly value={shareDisplay}
                  placeholder="auto" />
              </div>

              {/*  Warning if share < 100 — prompt to complete (c) and (d) */}
              {shareBelowHundred && (
                <div className="fd-share-warn">
                  <InfoOutlinedIcon sx={{ fontSize: 16, color: "#B7791F", flexShrink: 0, mt: "1px" }} />
                  <div>
                    <strong>Share is {fmtShare(shareNum)}%.</strong>{" "}
                    The remaining <strong>{fmtShare(100 - shareNum)}%</strong> must be accounted for
                    in section <strong>(c) Consumed in other production processes</strong> and/or
                    <strong> (d) Consumed for non-CBAM goods</strong> below.
                  </div>
                </div>
              )}

              {/*  iii — auto-derived Yes/No, read-only */}
              <div className="fd-field">
                <div className="fd-field-label">iii · Total production only for the market?</div>
                <div className="fd-field-unit">y/n</div>
                <input className="fd-input lblue" readOnly value={onlyMarketDisplay}
                  placeholder="auto" />
              </div>
              {forcedFalseByConsumption && (
                <div className="fd-sector-note" style={{ marginTop: -4, marginBottom: 8, fontSize: 10 }}>
                  Auto-set to "No" — amounts entered in (c) and/or (d).
                </div>
              )}

              {/* (c) Consumed in other processes */}
              <div className="fd-sub-label">(c) Consumed in other production processes</div>
              <div className="fd-rows">
                <div className="fd-row-head" style={{ gridTemplateColumns: "28px 1fr 110px 1fr" }}>
                  <div className="fd-rownum">#</div>
                  <div>Process</div>
                  <div>Unit</div>
                  <div>Amounts</div>
                </div>
                {proc.consumedOtherProcesses.slice(0, consumedToShow).map((row, i) => (
                  <div key={i} className="fd-row" style={{ gridTemplateColumns: "28px 1fr 110px 1fr" }}>
                    <div className="fd-rownum">{String(i+1).padStart(2,"0")}</div>
                    <select className="fd-select" value={row.processId || ""}
                      onChange={(e) => update(pi, `consumedOtherProcesses.${i}.processId`, e.target.value)}>
                      <option value="">—</option>
                      {otherProcs.map((p) => (
                        <option key={p.processId} value={p.processId}>{p.processId} — {p.title || "Unnamed"}</option>
                      ))}
                    </select>
                    <select className="fd-select" value={row.unit || ""}
                      onChange={(e) => update(pi, `consumedOtherProcesses.${i}.unit`, e.target.value)}>
                      <option value="">—</option>
                      {UNIT_GROUPS.MASS_OR_VOLUME.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                    <input className="fd-input" type="number" value={row.amounts || ""}
                      onChange={(e) => update(pi, `consumedOtherProcesses.${i}.amounts`, e.target.value)} />
                  </div>
                ))}
              </div>
              {!isExpanded(pi, "cons") && (
                <button className="fd-expander" onClick={() => toggleExpanded(pi, "cons")}>
                  + Show all 9 rows (7 more)
                </button>
              )}
              {isExpanded(pi, "cons") && (
                <button className="fd-expander" onClick={() => toggleExpanded(pi, "cons")}>
                  − Collapse to first 2 rows
                </button>
              )}

              {/* (d) Consumed for non-CBAM goods */}
              <div className="fd-sub-label">(d) Consumed for non-CBAM goods (within installation)</div>
              <div className="fd-field">
                <div className="fd-field-label">Amount &amp; unit</div>
                <select className="fd-select" value={proc.consumedNonCbam?.unit || ""}
                  onChange={(e) => update(pi, "consumedNonCbam.unit", e.target.value)}>
                  <option value="">—</option>
                  {UNIT_GROUPS.MASS_OR_VOLUME.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <input className="fd-input" type="number" value={proc.consumedNonCbam?.amounts || ""}
                  onChange={(e) => update(pi, "consumedNonCbam.amounts", e.target.value)} />
              </div>

              {/* (e) Control balance */}
              <div className={`fd-control ${balanceOk ? "ok" : "bad"}`}>
                <div>
                  <div className="label">
                    (e) Control balance {balanceOk ? "passed" : "off"} — {balanceOk ? "all production accounted for" : "imbalance detected"}
                  </div>
                  <div className="formula">(a) − (b.i) − (c) − (d) — must equal 0</div>
                </div>
                <div className="val">{balance} {balanceOk ? "✓" : "✗"}</div>
              </div>
            </div>
          )}

          {getSubTab(pi) === "emissions" && (
            <div className="fd-subtab-panel">
              <div className="fd-sub-label">(g) Directly attributable emissions</div>
              {isAluminium && (
                <div className="fd-alu-hint">
                  <InfoOutlinedIcon sx={{ fontSize: 13 }} />
                  Aluminium: remember PFC contributions are captured separately in Form B section (b).
                </div>
              )}
              <div className="fd-field" style={{ marginBottom: 12 }}>
                <div className="fd-field-label">Directly attributable emissions</div>
                <div className="fd-field-unit">{UNITS.TCO2E}</div>
                <input className="fd-input" type="number"
                  value={proc.directlyAttributableEmissions || ""}
                  onChange={(e) => update(pi, "directlyAttributableEmissions", e.target.value)} />
              </div>

              <div className="fd-sub-label">Applicable elements</div>

              {/* Measurable heat */}
              <div className="fd-element-block">
                <div className="fd-toggle-row" style={{ background: "transparent", border: "none", padding: 0, marginBottom: 0 }}>
                  <div>
                    <div className="label">Measurable heat</div>
                    <div className="sublabel">Heat imported/exported across process boundary</div>
                  </div>
                  <button
                    className={`fd-toggle ${heatOn ? "on" : ""}`}
                    onClick={() => update(pi, "applicableElements.measurableHeat", heatOn ? "FALSE" : "TRUE")}
                  />
                </div>
                {heatOn && (
                  <div className="fd-element-fields">
                    <div className="fd-rows">
                      <div className="fd-row-head" style={{ gridTemplateColumns: "1fr 70px 1fr 1fr" }}>
                        <div></div>
                        <div>Unit</div>
                        <div>Imported</div>
                        <div>Exported</div>
                      </div>
                      <div className="fd-row" style={{ gridTemplateColumns: "1fr 70px 1fr 1fr" }}>
                        <div className="fd-field-label">Amount</div>
                        <div className="fd-field-unit">{UNITS.TJ}</div>
                        <input className="fd-input" type="number"
                          value={proc.measurableHeat?.amountImported || ""}
                          onChange={(e) => update(pi, "measurableHeat.amountImported", e.target.value)} />
                        <input className="fd-input" type="number"
                          value={proc.measurableHeat?.amountExported || ""}
                          onChange={(e) => update(pi, "measurableHeat.amountExported", e.target.value)} />
                      </div>
                      <div className="fd-row" style={{ gridTemplateColumns: "1fr 70px 1fr 1fr" }}>
                        <div className="fd-field-label">Emission factor</div>
                        <div className="fd-field-unit">{UNITS.TCO2_PER_TJ}</div>
                        <input className="fd-input" type="number"
                          value={proc.measurableHeat?.emissionFactorImported || ""}
                          onChange={(e) => update(pi, "measurableHeat.emissionFactorImported", e.target.value)} />
                        <input className="fd-input" type="number"
                          value={proc.measurableHeat?.emissionFactorExported || ""}
                          onChange={(e) => update(pi, "measurableHeat.emissionFactorExported", e.target.value)} />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Waste gases */}
              <div className="fd-element-block">
                <div className="fd-toggle-row" style={{ background: "transparent", border: "none", padding: 0, marginBottom: 0 }}>
                  <div>
                    <div className="label">Waste gases</div>
                    <div className="sublabel">Gases used as fuel or feedstock</div>
                  </div>
                  <button
                    className={`fd-toggle ${wasteOn ? "on" : ""}`}
                    onClick={() => update(pi, "applicableElements.wasteGases", wasteOn ? "FALSE" : "TRUE")}
                  />
                </div>
                {wasteOn && (
                  <div className="fd-element-fields">
                    <div className="fd-rows">
                      <div className="fd-row-head" style={{ gridTemplateColumns: "1fr 70px 1fr 1fr" }}>
                        <div></div>
                        <div>Unit</div>
                        <div>Imported</div>
                        <div>Exported</div>
                      </div>
                      <div className="fd-row" style={{ gridTemplateColumns: "1fr 70px 1fr 1fr" }}>
                        <div className="fd-field-label">Amount</div>
                        <div className="fd-field-unit">{UNITS.TJ}</div>
                        <input className="fd-input" type="number"
                          value={proc.wasteGases?.amountImported || ""}
                          onChange={(e) => update(pi, "wasteGases.amountImported", e.target.value)} />
                        <input className="fd-input" type="number"
                          value={proc.wasteGases?.amountExported || ""}
                          onChange={(e) => update(pi, "wasteGases.amountExported", e.target.value)} />
                      </div>
                      <div className="fd-row" style={{ gridTemplateColumns: "1fr 70px 1fr 1fr" }}>
                        <div className="fd-field-label">Emission factor</div>
                        <div className="fd-field-unit">{UNITS.TCO2_PER_TJ}</div>
                        <input className="fd-input" type="number"
                          value={proc.wasteGases?.emissionFactorImported || ""}
                          onChange={(e) => update(pi, "wasteGases.emissionFactorImported", e.target.value)} />
                        <input className="fd-input" type="number"
                          value={proc.wasteGases?.emissionFactorExported || ""}
                          onChange={(e) => update(pi, "wasteGases.emissionFactorExported", e.target.value)} />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Indirect emissions */}
              <div className="fd-element-block">
                <div className="fd-toggle-row" style={{ background: "transparent", border: "none", padding: 0, marginBottom: 0 }}>
                  <div>
                    <div className="label">
                      Indirect emissions (electricity)
                      {indirectRule.locked && (
                        <Tooltip title={indirectRule.hint} arrow placement="top">
                          <LockOutlinedIcon sx={{ fontSize: 13, color: "#64748b", marginLeft: 4 }} />
                        </Tooltip>
                      )}
                    </div>
                    <div className="sublabel">{indirectRule.hint || "Electricity consumed during production"}</div>
                  </div>
                  <button
                    className={`fd-toggle ${indirOn ? "on" : ""} ${indirectRule.locked ? "locked" : ""}`}
                    disabled={indirectRule.locked}
                    onClick={() => {
                      if (indirectRule.locked) return;
                      update(pi, "applicableElements.indirectEmissions", indirOn ? "FALSE" : "TRUE");
                    }}
                  />
                </div>
                {indirOn && (
                  <div className="fd-element-fields">
                    <div className="fd-field">
                      <div className="fd-field-label">Electricity consumption</div>
                      <div className="fd-field-unit">{UNITS.MWH}</div>
                      <input className="fd-input" type="number"
                        value={proc.electricityConsumption?.consumption || ""}
                        onChange={(e) => update(pi, "electricityConsumption.consumption", e.target.value)} />
                    </div>
                    <div className="fd-field">
                      <div className="fd-field-label">Emission factor</div>
                      <div className="fd-field-unit">{UNITS.TCO2_PER_MWH}</div>
                      <input className="fd-input" type="number"
                        value={proc.electricityConsumption?.emissionFactor || ""}
                        onChange={(e) => update(pi, "electricityConsumption.emissionFactor", e.target.value)} />
                    </div>
                    <div className="fd-field">
                      <div className="fd-field-label">Source of emission factor</div>
                      <div className="fd-field-unit">—</div>
                      <select className="fd-select"
                        value={proc.electricityConsumption?.source || ""}
                        onChange={(e) => update(pi, "electricityConsumption.source", e.target.value)}>
                        <option value="">Select source...</option>
                        {["D.4(a)", "D.4(b)", "D.4.1", "D.4.2", "D.4.3.1", "D.4.3.2", "Mix"].map((o) => (
                          <option key={o} value={o}>{o}</option>
                        ))}
                      </select>
                    </div>
                    <div className="fd-sub-label" style={{ marginTop: 10 }}>Electricity exported</div>
                    <div className="fd-field">
                      <div className="fd-field-label">Amounts exported</div>
                      <div className="fd-field-unit">{UNITS.MWH}</div>
                      <input className="fd-input" type="number"
                        value={proc.electricityExported?.amountsExported || ""}
                        onChange={(e) => update(pi, "electricityExported.amountsExported", e.target.value)} />
                    </div>
                    <div className="fd-field">
                      <div className="fd-field-label">Emission factor</div>
                      <div className="fd-field-unit">{UNITS.TCO2_PER_MWH}</div>
                      <input className="fd-input" type="number"
                        value={proc.electricityExported?.emissionFactor || ""}
                        onChange={(e) => update(pi, "electricityExported.emissionFactor", e.target.value)} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* ─── RIGHT: sticky SEE panel ─── */}
        <div className="fd-sticky-col">
          <div className="fd-sticky">
            <div className="fd-see-card">
              <div className="fd-see-card-title">
                {proc.processId} · {getProcessProgress(proc).progress}% complete
              </div>
              <div className="fd-see-item">
                <span className="k">DirEm net</span>
                <span>
                  <span className={`v ${!seeHasValue ? "zero" : ""}`}>{fmt(proc.dirEm_net)}</span>
                  <span className="unit">tCO₂e</span>
                </span>
              </div>
              <div className="fd-see-item">
                <span className="k">SEE direct</span>
                <span>
                  <span className={`v ${!seeHasValue ? "zero" : ""}`}>{fmt(proc.see_direct)}</span>
                  <span className="unit">tCO₂e/t</span>
                </span>
              </div>
              <div className="fd-see-item">
                <span className="k">Indirect Em</span>
                <span>
                  <span className={`v ${!indirOn ? "zero" : ""}`}>
                    {indirOn ? fmt(proc.indir_em) : "N/A"}
                  </span>
                  <span className="unit">tCO₂e</span>
                </span>
              </div>
              <div className="fd-see-item">
                <span className="k">SEE indirect</span>
                <span>
                  <span className={`v ${!indirOn ? "zero" : ""}`}>
                    {indirOn ? fmt(proc.see_indirect) : "N/A"}
                  </span>
                  <span className="unit">tCO₂e/t</span>
                </span>
              </div>
              <div className="fd-see-grand">
                <div className="k">SEE total · direct + indirect</div>
                <div>
                  <span className={`v ${!seeHasValue ? "zero" : ""}`}>{fmt(proc.see_total)}</span>
                  <span className="unit">tCO₂e/t</span>
                </div>
              </div>
            </div>

            <div className="fd-process-meta" style={{ margin: 0 }}>
              <div className="fd-meta-item">
                <div className="lbl">Aggregated goods</div>
                <div className="val" style={{ fontSize: 12 }}>{proc.aggregatedGoodsCategory || "—"}</div>
              </div>
              <div className="fd-meta-item">
                <div className="lbl">Production total</div>
                <div className="val" style={{ fontSize: 12 }}>{fmt(proc.productionLevelsTotal)} t</div>
              </div>
              <div className="fd-meta-item">
                <div className="lbl">Control balance</div>
                <div className="val" style={{ fontSize: 12, color: balanceOk ? "#15803d" : "#b91c1c" }}>
                  {balance} {balanceOk ? "✓" : "✗"}
                </div>
              </div>
              <div className="fd-meta-item">
                <div className="lbl">Applicable</div>
                <div className="val" style={{ fontSize: 11 }}>
                  {[heatOn && "Heat", wasteOn && "Gas", indirOn && "Indirect"].filter(Boolean).join(" · ") || "None"}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const activeProc = processes[activeProcessIdx];
  const isLastProcess = activeProcessIdx === processes.length - 1;

  return (
    <>
      <style>{STYLES}</style>
      <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#f8fafc" }}>
        <AuditSidebar displayAuditId={auditId} />
        <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
          <div className="fd">
            <div className="fd-header-slim">
              <div className="title">Production Levels &amp; Attributed Emissions</div>
            </div>
            <div className="fd-body">
              {installSector && (
                <div className="fd-sector-bar">
                  <span>Sector:</span>
                  <span className="fd-sector-chip">{installSector}</span>
                  {indirectRule.hint && (
                    <Tooltip title={indirectRule.hint} arrow placement="top">
                      <span className="fd-sector-note">indirect: {indirectRule.locked ? "required" : "optional"}</span>
                    </Tooltip>
                  )}
                </div>
              )}

              <div className="fd-tabs">
                {processes.map((p, i) => (
                  <div
                    key={p.processId}
                    className={`fd-tab ${i === activeProcessIdx ? "active" : ""}`}
                    onClick={() => setActiveProcessIdx(i)}
                  >
                    <span className="fd-tab-num">{p.processId}</span>
                    <span className="fd-tab-title">{p.title || "Unnamed"}</span>
                    <span className={`fd-tab-badge ${tabBadgeClass(getProcessProgress(p).progress)}`}>
                      {getProcessProgress(p).progress}%
                    </span>
                  </div>
                ))}
              </div>

              {activeProc && renderProcess(activeProc, activeProcessIdx)}

              <div className="fd-footer-bar">
                <button className="fd-btn-prev" onClick={handleBack} disabled={isSaving}>
                  ← Back
                </button>
                {isLastProcess ? (
                  <button className="fd-btn-save" onClick={handleSaveAndNext} disabled={isSaving}>
                    {isSaving ? "Saving..." : "Save & Continue →"}
                  </button>
                ) : (
                  <button className="fd-btn-save" onClick={handleNextProcess} disabled={isSaving}>
                    {isSaving ? "Saving..." : `Next process (${processes[activeProcessIdx + 1]?.processId}) →`}
                  </button>
                )}
              </div>
            </div>
            {toast && (
              <div className={`fd-toast ${toast.type === "error" ? "error" : ""}`}>
                {toast.type === "error" ? "×" : "✓"} {toast.msg}
              </div>
            )}
          </div>
        </Box>
      </Box>
    </>
  );
};

export default FormD;
